import {
  GEOLOGY_ASSETS,
  GEOLOGY_ASSET_IDS,
  GEOLOGY_OBJECTS,
  type GeologyAssetId,
} from "@endclock/shared";
import { loadStaticGlb } from "../assets/gltf.js";
import type { Camera } from "../engine/camera.js";
import { type GpuMesh, type Renderer, type StaticRenderMaterial } from "../engine/renderer.js";
import { ReusableInstanceBatch, createStaticObjectChunks } from "../world/static-instances.js";

interface GeologyManifestEntry {
  readonly id: GeologyAssetId;
  readonly url: string;
  readonly triangleCount: number;
  readonly collision: { readonly shape: "none" | "circle"; readonly radius: number };
  readonly boundsRadius: number;
}

interface GeologyManifest {
  readonly version: number;
  readonly format: string;
  readonly assets: readonly GeologyManifestEntry[];
}

interface LoadedGeologyPrimitive {
  readonly mesh: GpuMesh;
  readonly baseColor: readonly [number, number, number, number];
  readonly material: StaticRenderMaterial;
}

interface LoadedGeologyAsset {
  readonly primitives: readonly LoadedGeologyPrimitive[];
  readonly boundsRadius: number;
  readonly triangleCount: number;
}

export type GeologyLoadState = "loading" | "ready" | "failed";

export class GeologyWorld {
  objects: readonly (typeof GEOLOGY_OBJECTS)[number][] = GEOLOGY_OBJECTS;
  state: GeologyLoadState = "loading";
  loadError: string | undefined;
  private readonly assets = new Map<GeologyAssetId, LoadedGeologyAsset>();
  private readonly batches = new Map<GeologyAssetId, ReusableInstanceBatch>();
  private chunks = createStaticObjectChunks(this.objects, (object) => ({
    centerY: object.y + GEOLOGY_ASSETS[object.assetId].boundsRadius * object.scale * 0.35,
    radius: GEOLOGY_ASSETS[object.assetId].boundsRadius * object.scale,
  }));

  constructor(private readonly renderer: Renderer) {
    for (const id of GEOLOGY_ASSET_IDS) this.batches.set(id, new ReusableInstanceBatch());
    renderer.metrics.totalGeologyInstances = this.objects.length;
    void this.load();
  }

  setObjects(objects: readonly (typeof GEOLOGY_OBJECTS)[number][]): void {
    this.renderer.invalidateLocalShadowCache();
    this.objects = objects;
    this.chunks = createStaticObjectChunks(objects, (object) => ({
      centerY: object.y + GEOLOGY_ASSETS[object.assetId].boundsRadius * object.scale * 0.35,
      radius: GEOLOGY_ASSETS[object.assetId].boundsRadius * object.scale,
    }));
    this.renderer.metrics.totalGeologyInstances = objects.length;
    if (this.state === "ready") {
      this.renderer.metrics.totalGeologyTriangles = objects.reduce(
        (sum, object) => sum + (this.assets.get(object.assetId)?.triangleCount ?? 0),
        0,
      );
    }
  }

  render(camera: Camera): void {
    if (this.state !== "ready") return;
    for (const batch of this.batches.values()) batch.reset();
    for (const chunk of this.chunks) {
      if (!camera.isSphereVisible(chunk.centerX, chunk.centerY, chunk.centerZ, chunk.radius)) continue;
      for (const object of chunk.objects) {
        const asset = this.assets.get(object.assetId);
        if (!asset || !camera.isSphereVisible(object.x, object.y, object.z, asset.boundsRadius * object.scale)) continue;
        const primitive = asset.primitives[0]!;
        this.batches.get(object.assetId)!.pushTerrainAligned(
          object,
          object.terrainNormal,
          object.normalAlignment,
          primitive.baseColor,
          0.08,
          0.62,
        );
        this.renderer.metrics.visibleGeologyInstances += 1;
      }
    }
    for (const assetId of GEOLOGY_ASSET_IDS) {
      const batch = this.batches.get(assetId)!;
      const asset = this.assets.get(assetId);
      if (!asset || batch.count === 0) continue;
      for (const primitive of asset.primitives) {
        this.renderer.drawStaticInstanced(primitive.mesh, batch.data, batch.count, primitive.material, "geology");
      }
    }
  }

  private async load(): Promise<void> {
    try {
      const response = await fetch("/assets/geology/manifest.json");
      if (!response.ok) throw new Error("Geology manifest request failed");
      const manifest = (await response.json()) as GeologyManifest;
      if (manifest.version !== 1 || manifest.assets.length !== GEOLOGY_ASSET_IDS.length) throw new Error("Invalid geology manifest");
      const loaded = await Promise.all(
        manifest.assets.map(async (entry) => {
          const definition = GEOLOGY_ASSETS[entry.id];
          if (!definition || Math.abs(definition.collisionRadius - entry.collision.radius) > 0.0001) {
            throw new Error(`Geology metadata mismatch: ${entry.id}`);
          }
          const asset = await loadStaticGlb(entry.url);
          const triangleCount = asset.primitives.reduce((sum, primitive) => sum + primitive.geometry.indices.length / 3, 0);
          if (asset.id !== entry.id || triangleCount !== entry.triangleCount || asset.primitives.length !== 1) {
            throw new Error(`Geology asset mismatch: ${entry.id}`);
          }
          if (asset.collision.shape !== entry.collision.shape || Math.abs(asset.boundsRadius - entry.boundsRadius) > 0.0001) {
            throw new Error(`Geology proxy mismatch: ${entry.id}`);
          }
          return [entry.id, asset, triangleCount] as const;
        }),
      );
      let totalTriangles = 0;
      for (const [id, asset, triangleCount] of loaded) {
        const primitives: LoadedGeologyPrimitive[] = [];
        for (const primitive of asset.primitives) {
          if (primitive.material.alphaMode !== "OPAQUE" || primitive.material.baseColorTexture) {
            throw new Error(`Geology material mismatch: ${id}`);
          }
          const mesh = this.renderer.createMesh(primitive.geometry);
          this.renderer.metrics.geologyBufferBytes += mesh.bufferBytes;
          primitives.push({
            mesh,
            baseColor: primitive.material.baseColor,
            material: { alphaCutoff: 0, doubleSided: false },
          });
        }
        this.assets.set(id, { primitives, boundsRadius: asset.boundsRadius, triangleCount });
      }
      for (const object of this.objects) totalTriangles += this.assets.get(object.assetId)!.triangleCount;
      this.renderer.metrics.totalGeologyTriangles = totalTriangles;
      this.state = "ready";
    } catch (error) {
      this.state = "failed";
      this.loadError = error instanceof Error ? error.message : "Unknown geology load error";
      console.error(this.loadError);
    }
  }
}
